import { useState, useEffect } from 'react';
import { Modal } from '@/shared/components/ui/Modal';
import { Input } from '@/shared/components/ui/Input';
import { Button } from '@/shared/components/ui/Button';
import { Badge } from '@/shared/components/ui/Badge';
import { useToast } from '@/shared/components/ui/Toast';
import { BackdatedIndicator } from '@/shared/components/ui/BackdatedIndicator';
import { useMarkAsReceived } from '../hooks/useReceiving';
import { getTodayDateString, compareDateStrings } from '@/shared/utils/dateUtils';
import type { FrameReplenishmentItem } from '@/shared/types/domain.types';

interface ReceiveBatchFormProps {
  isOpen: boolean;
  onClose: () => void;
  frames: FrameReplenishmentItem[];
  onSuccess: () => void;
}

interface PatientFrameData {
  frameId: string;
  patientName: string;
  trayNumber: string;
}

export function ReceiveBatchForm({
  isOpen,
  onClose,
  frames,
  onSuccess,
}: ReceiveBatchFormProps) {
  const [receivedDate, setReceivedDate] = useState(getTodayDateString());
  const [patientFrames, setPatientFrames] = useState<PatientFrameData[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { showToast } = useToast();
  const markAsReceived = useMarkAsReceived();

  // Initialize patient frame data
  useEffect(() => {
    if (frames.length > 0) {
      const patientData = frames
        .filter((f) => f.order_type === 'PATIENT')
        .map((f) => ({
          frameId: f.id,
          patientName: f.patient_name || '',
          trayNumber: f.tray_number || '',
        }));
      setPatientFrames(patientData);
    }
  }, [frames]);

  // Get earliest ordered_date for validation
  const earliestOrderedDate = frames.reduce((earliest, frame) => {
    if (!frame.ordered_date) return earliest;
    if (!earliest) return frame.ordered_date;
    return compareDateStrings(frame.ordered_date, earliest) < 0 ? frame.ordered_date : earliest;
  }, '');

  // Categorize frames
  const stockFrames = frames.filter((f) => f.order_type === 'STOCK');
  const patientFramesOriginal = frames.filter((f) => f.order_type === 'PATIENT');
  const hasMixedTypes = stockFrames.length > 0 && patientFramesOriginal.length > 0;
  const hasPatientFrames = patientFramesOriginal.length > 0;

  // Get unique manufacturers and locations for display
  const uniqueManufacturers = [
    ...new Set(frames.map((f) => f.manufacturer?.name).filter(Boolean)),
  ];
  const uniqueLocations = [...new Set(frames.map((f) => f.location?.name).filter(Boolean))];
  const multipleLocations = uniqueLocations.length > 1;

  const handlePatientDataChange = (frameId: string, field: 'patientName' | 'trayNumber', value: string) => {
    setPatientFrames((prev) =>
      prev.map((pf) =>
        pf.frameId === frameId
          ? { ...pf, [field]: value }
          : pf
      )
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    // Validation
    const newErrors: Record<string, string> = {};

    // Validate received date
    if (!receivedDate) {
      newErrors.received_date = 'Received date is required';
    } else {
      // Check if future date
      if (compareDateStrings(receivedDate, getTodayDateString()) > 0) {
        newErrors.received_date = 'Received date cannot be in the future';
      }
      // Check if before ordered date
      if (earliestOrderedDate && compareDateStrings(receivedDate, earliestOrderedDate) < 0) {
        newErrors.received_date = 'Received date cannot be earlier than ordered date';
      }
    }

    // Validate patient frames
    patientFrames.forEach((pf) => {
      if (!pf.patientName.trim()) {
        newErrors[`patient_${pf.frameId}`] = 'Patient name is required';
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      // Mark each frame as received with its specific data
      const promises = frames.map((frame) => {
        const patientData = patientFrames.find((pf) => pf.frameId === frame.id);

        return markAsReceived.mutateAsync({
          frame_ids: [frame.id],
          received_date: receivedDate,
          order_type: frame.order_type,
          patient_name: patientData?.patientName.trim() || undefined,
          tray_number: patientData?.trayNumber.trim() || undefined,
        });
      });

      await Promise.all(promises);

      showToast(
        `${frames.length} frame${frames.length !== 1 ? 's' : ''} marked as received`,
        'success'
      );

      // Reset form
      setReceivedDate(getTodayDateString());
      setPatientFrames([]);
      setErrors({});

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error marking frames as received:', error);
      showToast(
        error instanceof Error ? error.message : 'Failed to mark frames as received',
        'error'
      );
    }
  };

  const handleClose = () => {
    if (!markAsReceived.isPending) {
      setErrors({});
      onClose();
    }
  };

  const isBackdated = compareDateStrings(receivedDate, getTodayDateString()) < 0;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Mark Frames as Received" size="lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Summary */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="font-semibold text-blue-900 mb-2">Receiving Summary</h3>
          <div className="text-sm space-y-1">
            <div className="flex justify-between">
              <span className="text-blue-700">Number of Frames:</span>
              <span className="font-medium text-blue-900">
                {frames.length} frame{frames.length !== 1 ? 's' : ''}
              </span>
            </div>
            {stockFrames.length > 0 && (
              <div className="flex justify-between">
                <span className="text-blue-700">STOCK Frames:</span>
                <span className="font-medium text-blue-900">{stockFrames.length}</span>
              </div>
            )}
            {patientFramesOriginal.length > 0 && (
              <div className="flex justify-between">
                <span className="text-blue-700">PATIENT Frames:</span>
                <span className="font-medium text-blue-900">{patientFramesOriginal.length}</span>
              </div>
            )}
            {uniqueManufacturers.length > 0 && (
              <div className="flex justify-between">
                <span className="text-blue-700">Manufacturer{uniqueManufacturers.length > 1 ? 's' : ''}:</span>
                <span className="font-medium text-blue-900">
                  {uniqueManufacturers.length === 1
                    ? uniqueManufacturers[0]
                    : `${uniqueManufacturers.length} manufacturers`}
                </span>
              </div>
            )}
            {uniqueLocations.length > 0 && (
              <div className="flex justify-between">
                <span className="text-blue-700">Location{uniqueLocations.length > 1 ? 's' : ''}:</span>
                <span className="font-medium text-blue-900">
                  {uniqueLocations.length === 1
                    ? uniqueLocations[0]
                    : `${uniqueLocations.length} locations`}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Mixed Order Types Warning */}
        {hasMixedTypes && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <span className="text-yellow-600 text-lg">⚠️</span>
              <div className="flex-1">
                <p className="text-sm font-semibold text-yellow-800 mb-1">Mixed Order Types</p>
                <p className="text-sm text-yellow-700">
                  This batch includes patient frames. Please be sure patient frames are placed in their trays.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Multiple Locations Warning */}
        {multipleLocations && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <span className="text-yellow-600 text-lg">⚠️</span>
              <div className="flex-1">
                <p className="text-sm font-semibold text-yellow-800 mb-1">
                  Multiple Locations Detected
                </p>
                <p className="text-sm text-yellow-700">
                  You are receiving frames from {uniqueLocations.length} different locations in this batch
                  ({uniqueLocations.join(', ')}). Please ensure frames are delivered to the correct locations.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Received Date Input */}
        <Input
          label="Received Date"
          type="date"
          value={receivedDate}
          onChange={(e) => setReceivedDate(e.target.value)}
          required
          error={errors.received_date}
          helperText="Date cannot be in the future or before ordered date"
          max={getTodayDateString()}
          disabled={markAsReceived.isPending}
          autoFocus
        />

        {/* Backdated Warning */}
        {isBackdated && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <BackdatedIndicator isBackdated={true} />
              <p className="text-sm text-orange-700">
                This date is in the past. The frames will be marked as backdated for audit
                purposes.
              </p>
            </div>
          </div>
        )}

        {/* Patient Frames Section (if any) */}
        {hasPatientFrames && (
          <div className="border border-gray-200 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-3">Patient Frame Information</h3>
            <p className="text-sm text-gray-600 mb-4">
              Review and update patient information for each PATIENT frame below.
            </p>
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {patientFramesOriginal.map((frame) => {
                const patientData = patientFrames.find((pf) => pf.frameId === frame.id);
                if (!patientData) return null;

                return (
                  <div
                    key={frame.id}
                    className="bg-gray-50 border border-gray-200 rounded-lg p-4"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-gray-900">
                            {frame.brand} - {frame.frame_name_or_model}
                          </span>
                          <Badge variant="purple" size="sm">
                            PATIENT
                          </Badge>
                        </div>
                        <div className="text-sm text-gray-600">
                          {frame.color} • Size {frame.eye_size} • <span className="hidden lg:inline">{frame.location?.name}</span><span className="lg:hidden">{frame.location?.short_code || frame.location?.name}</span>
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <Input
                        label="Patient Name"
                        type="text"
                        value={patientData.patientName}
                        onChange={(e) =>
                          handlePatientDataChange(frame.id, 'patientName', e.target.value)
                        }
                        placeholder="Enter patient name"
                        required
                        error={errors[`patient_${frame.id}`]}
                        disabled={markAsReceived.isPending}
                      />
                      <Input
                        label="Tray Number (Optional)"
                        type="text"
                        value={patientData.trayNumber}
                        onChange={(e) =>
                          handlePatientDataChange(frame.id, 'trayNumber', e.target.value)
                        }
                        placeholder="Enter tray number"
                        disabled={markAsReceived.isPending}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* STOCK Frames Notice (if no patient frames) */}
        {!hasPatientFrames && stockFrames.length > 0 && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <p className="text-sm text-gray-700">
              All selected frames are STOCK frames. No patient information needed.
            </p>
          </div>
        )}

        {/* Confirmation Message */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <p className="text-sm text-gray-700">
            Are you sure you want to mark these {frames.length} frame
            {frames.length !== 1 ? 's' : ''} as received? This will:
          </p>
          <ul className="mt-2 text-sm text-gray-600 list-disc list-inside space-y-1">
            <li>Change their status from ORDERED to RECEIVED</li>
            <li>Set the received date to {receivedDate}</li>
            {hasPatientFrames && <li>Update patient information for PATIENT frames</li>}
            <li>Remove them from the receiving list</li>
          </ul>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 pt-4">
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            disabled={markAsReceived.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={markAsReceived.isPending || !receivedDate}
          >
            {markAsReceived.isPending ? 'Marking as Received...' : 'Mark as Received'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
